import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addPrivateProsecutionFeedback,
  editPrivateProsecution,
} from "services/privateProsecutionService";
import { PrivateProsecution } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

interface Props {
  privateProsecution?: PrivateProsecution;
  setRefetch: () => void;
  onCancel: () => void;
}

const RespondBackFeedback: React.FC<Props> = ({
  privateProsecution,
  onCancel,
  setRefetch,
}) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [comment, setComment] = useState("");

  const validateForm = () => {
    if (!comment) return t("comment_is_required");
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    addPrivateProsecutionFeedback({
      feedback_text: comment,
      created_by: loginData?.full_name,
      private_prosecution: privateProsecution?.id || null,
    })
      .then(() => {
        if (privateProsecution?.id) {
          editPrivateProsecution({
            id: privateProsecution?.id,
            status: "rejected",
            forwarding_status: "on_prosecutor_desk",
          })
            .then(() => {
              showTooltip(t("successfully_updated"), "success");
              setRefetch();
            })
            .catch((error) => {
              showTooltip(
                error?.response?.data?.detail || errorHandlerOnForm(error),
                "error",
              );
            });
        }
        showTooltip(t("private_prosecution_submitted_successfully"), "success");
        setRefetch();
        onCancel();
      })
      .catch((err) => {
        showTooltip(errorHandlerOnForm(err?.response?.data), "error");
        setLoading(false);
      })
      .then(() => {
        setLoading(false);
      });
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onCancel();
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-[400px] max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-800">
          Respond Back Feedback
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="flex flex-col col-span-2">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Comment
              </label>
              <textarea
                rows={4}
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-6 flex justify-between gap-2">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded w-full hover:bg-gray-300"
              onClick={onCancel}
              type="button"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`border border-[#E90101] bg-[#E90101] px-10 py-2 text-white 
                        font-[Poppins] cursor-pointer font-[500] w-full
                        text-base rounded-md hover:bg-white hover:text-[#E90101]
                        ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RespondBackFeedback;
